import { get } from "lodash";
import { PathArray } from "common/utils/formStructure";
import { spacing } from "../../../../../../../../stitches.config";
import { Button } from "../../../../../../../Common/Buttons/Button";
import { useBuilderFormContext, useBuilderWatch } from "../../../form/form";
import type { InputSettingsFormProps } from "../FieldSettingsView";
import { useIsFieldRequired } from "../required";
import { LabelValueOptionsInput } from "../shared/LabelValueOptionsInput";
import type { UnitsOptions } from "./types";
import {
  addNullOption,
  getUnitsDefaultValuePaths,
  getUnitsOptionsPath,
  isNonNullOption,
  removeNullOption,
  unitsOptionsToDropdownOptions,
} from "./utils";

type UnitsOptionsInputProps = Pick<
  InputSettingsFormProps,
  "fieldPath" | "isReadOnly" | "isModuleInput"
>;

export function UnitsOptionsInput({
  fieldPath,
  isReadOnly,
  isModuleInput,
}: UnitsOptionsInputProps) {
  const { setValue } = useBuilderFormContext();

  const unitsOptionsPath = getUnitsOptionsPath(fieldPath);
  const unitsOptions = useBuilderWatch(unitsOptionsPath) as
    | UnitsOptions
    | undefined;

  // exclude the null option because it is added/removed automatically based on
  // the required state
  const nonNullUnitsOptions = unitsOptions?.filter(isNonNullOption) ?? [];

  const isFieldRequired = useIsFieldRequired(fieldPath);
  const ensureValidDefaultValue = useEnsureValidDefaultValue(fieldPath);

  function setUnitsOptions(newOptions: UnitsOptions) {
    ensureValidDefaultValue(newOptions);

    const sanitizedNewOptions =
      isFieldRequired ?
        removeNullOption(newOptions)
      : addNullOption(newOptions);

    setValue(unitsOptionsPath, sanitizedNewOptions, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }

  function addOption() {
    const newOptions = [...nonNullUnitsOptions, { const: "", title: "" }];
    setUnitsOptions(newOptions);
  }

  function updateOption(index: number, label: string, value: string) {
    const newOptions = [...nonNullUnitsOptions];
    newOptions[index] = { const: value, title: label };
    setUnitsOptions(newOptions);
  }

  function deleteOption(index: number) {
    const newOptions = [...nonNullUnitsOptions];
    newOptions.splice(index, 1);
    setUnitsOptions(newOptions);
  }

  const dropdownOptions = unitsOptionsToDropdownOptions(nonNullUnitsOptions);

  return (
    <div>
      <LabelValueOptionsInput
        options={
          dropdownOptions.length ? dropdownOptions : [{ label: "", value: "" }]
        }
        updateOption={updateOption}
        deleteOption={deleteOption}
        valueEditingDisabled={isModuleInput}
        disabled={isReadOnly}
      />
      <div style={{ paddingTop: spacing.s.value }}>
        <Button
          title="Add option"
          variant="outline"
          size="small"
          iconStart="plus"
          onPress={addOption}
          isDisabled={isModuleInput || isReadOnly}
        />
      </div>
    </div>
  );
}

function useEnsureValidDefaultValue(fieldPath: PathArray) {
  const { getValues, setValue } = useBuilderFormContext();
  const [unitsDefaultValuePath, unitsBuilderDefaultValuePath] =
    getUnitsDefaultValuePaths(fieldPath);

  return (nextOptions: UnitsOptions) => {
    const currentDefaultValue = get(
      getValues(),
      unitsDefaultValuePath,
    ) as Maybe<string>;

    if (
      currentDefaultValue &&
      !nextOptions.some(
        option => "const" in option && option.const === currentDefaultValue,
      )
    ) {
      setValue(unitsDefaultValuePath, undefined);
      setValue(unitsBuilderDefaultValuePath, undefined);
    }
  };
}
